import { useCart } from '@/hooks/useCart';
import { ApiState } from '@/models/generics';
import {
	CART_TYPE,
	ICartItem,
	TVerticalCart,
} from '@/services/cart/models/cart';
import { Box, Divider } from '@mui/material';
import { useEffect, useState } from 'react';
import CartItemListHeader from './CartItemsListHeader';
import RemoveCartItemConfirmationDrawer from './RemoveCartItemConfirmationDrawer';
import CartItemList from './CartItemList';
import Vas from '../../Vas';

import { cartService } from '@/services/cart';
import { emitMoeEvent } from '@/utils/dataLayer';
import { useNotification } from '@/hooks/useNotification';
import { NotificationSeverity } from '@/components/NotifyToast';
import { useLogin } from '@/hooks/useLogin';
import { withFullScreenLoader } from '@/hoc/withFullScreenLoader';
import { useFullScreenLoader } from '@/hooks/useFullScreenLoader';

import { useLencoDispatch } from '@/hooks/useLencoDispatch';

const cartNameMap: Record<CART_TYPE, string> = {
	[CART_TYPE.BUY]: 'Buy Cart',
	[CART_TYPE.RENT]: 'Rent Cart',
};

type VerticalCartProps = {
	cartType: CART_TYPE;
	cartItems: ICartItem[];
	bulkActionDrawerOpen?: boolean;
	bulkActionDrawerClose?: () => void;
	bulkActionDrawerData?: any;
	showBulkTenureSelector?: () => void;
	cartItemsCount: number;
	verticalCart?: TVerticalCart;
	hideOffers?: boolean;
	openCartOosActionDrawer: () => void;
	showLoader: any;
	hideLoader: any;
	screen?: string;
};

export type SelectedOffer = {
	code: string;
	id?: number;
};

const VerticalCart = (props: VerticalCartProps) => {
	const {
		id: cartId,
		cart,
		getCartAttributes,
		cartActions,
		rentCart,
		buyCart,
	} = useCart();
	const dispatch = useLencoDispatch();
	const { actions } = useNotification();
	const { loader } = useFullScreenLoader();
	const { isLoggedIn, loginActions } = useLogin();
	const [removeCartItemViewState, setRemoveCartItemViewState] =
		useState<ApiState>(ApiState.IDLE);
	const [moveToWishlistViewState, setMoveToWishlistViewState] =
		useState<ApiState>(ApiState.IDLE);
	const [selectedCartItemToDelete, setSelectedCartItemToDelete] =
		useState<ICartItem>();
	const [offersListVisible, setOffersListVisible] = useState<boolean>(false);
	const [offerBeingApplied, setOfferBeingApplied] = useState<SelectedOffer>();
	const [removeCouponApiState, setRemoveCouponApiState] = useState<ApiState>(
		ApiState.IDLE
	);
	const [applyOfferErrorMessage, setApplyOfferErrorMessage] =
		useState<string>();
	const [enteredOfferCode, setEnteredOfferCode] = useState<string>('');

	const [tenureLabels, setTenureLabels] = useState<any>();

	const availableOffersCount =
		(props.verticalCart?.offers?.applicableOffers?.length ?? 0) +
		(props.verticalCart?.offers?.nonApplicableOffers?.length ?? 0);

	const cartOffers =
		props.cartType === CART_TYPE.RENT ? rentCart?.offers : buyCart?.offers;

	function deleteCartItemButtonClicked(cartItem: ICartItem) {
		setSelectedCartItemToDelete(cartItem);
	}

	function closeDeleteCartItemConfirmation() {
		setSelectedCartItemToDelete(undefined);
	}

	function openOffersList() {
		if (!isLoggedIn) {
			loginActions.showLoginFlow();
			return;
		}
		setOffersListVisible(true);
	}

	function closeOffersList() {
		setOffersListVisible(false);
	}

	function emitItemRemovedMoeEvent(eventName: string) {
		emitMoeEvent(eventName, {
			product_id: selectedCartItemToDelete?.product.id,
			lineofproduct: selectedCartItemToDelete?.lineOfProduct,
			tenure_selected: selectedCartItemToDelete?.tenure,
			stock_flag: selectedCartItemToDelete?.isOutOfStock ? 'oos' : 'available',
		});
	}

	function emitOfferEvent(
		eventName: string,
		data: { buyTotal: number; rentTotal: number; total: number; code?: string }
	) {
		const attributes = getCartAttributes();

		emitMoeEvent(eventName, {
			// cart_type: props.cartType,
			lineofproduct: attributes.lineOfProductsString,
			// vertical: attributes.verticalsString,
			buy_products: attributes.buyProductIdsString,
			rent_products: attributes.rentalProductIdsString,
			total_cart_value: data?.total,
			buy_cart_value: data?.buyTotal,
			rent_cart_value: data?.rentTotal,
			buy_offer_config: props.cartType == CART_TYPE.BUY ? data?.code : '',
			rent_offer_config: props.cartType == CART_TYPE.RENT ? data?.code : '',
		});
	}

	async function removeCartItem() {
		try {
			setRemoveCartItemViewState(ApiState.LOADING);
			loader.show();

			const res = await cartActions.removeItemFromCart(
				selectedCartItemToDelete?.id as number,
				props.cartType
			);
			if (res.data?.success) {
				emitItemRemovedMoeEvent('Product Deleted');
				setRemoveCartItemViewState(ApiState.SUCCESS);
				actions.notifyUser({
					message: 'Item removed from cart',
					severity: NotificationSeverity.SUCCESS,
				});
			} else {
				setRemoveCartItemViewState(ApiState.FAILED);
				actions.notifyUser({
					message: 'Something went wrong!',
					severity: NotificationSeverity.WARNING,
				});
			}

			setSelectedCartItemToDelete(undefined);

			return;
		} catch (err: any) {
			console.error(err);
			setRemoveCartItemViewState(ApiState.FAILED);
			actions.notifyUser({
				message: err?.response?.data?.error?.message,
				severity: NotificationSeverity.ERROR,
			});
			return;
		} finally {
			loader.hide();
		}
	}

	async function wishlistItem() {
		if (!isLoggedIn) {
			loginActions.showLoginFlow();
			return;
		}

		setMoveToWishlistViewState(ApiState.LOADING);

		cartService
			.moveToWishlist(
				cartId as number,
				props.cartType,
				selectedCartItemToDelete?.id as number
			)
			.then((res) => {
				if (res.data.success) {
					emitItemRemovedMoeEvent('Product Wishlisted');
					cartActions.getCart();
					actions.notifyUser({
						message: 'Item moved to wishlist',
						severity: NotificationSeverity.SUCCESS,
					});
				}
				setSelectedCartItemToDelete(undefined);
			})
			.catch((err) => {
				actions.notifyUser({
					message: err?.response?.data?.error?.message,
					severity: NotificationSeverity.ERROR,
				});
			})
			.finally(() => setMoveToWishlistViewState(ApiState.COMPLETED));
	}

	useEffect(() => {
		if (offersListVisible) {
			emitOfferEvent('Offers and Discounts Page Opened', {
				buyTotal: buyCart?.total.value as number,
				rentTotal: rentCart?.total.value as number,
				total: cart?.total.value as number,
			});
		}
	}, [offersListVisible]);

	useEffect(() => {
		let tenureData: any = {
			monthly: [],
			upfront: [],
		};

		let tenureMap: any = {};
		// if (props?.ver && props?.upfrontOffers?.tenureSelector) {

		// 	Object.values(props?.upfrontOffers?.tenureSelector)?.map((d:any)=>{
		// 		d?.paymentPlans?.map((plans:any)=>{
		// 			if(plans?.tenure == 1){
		// 				tenureData.monthly.push({
		// 					tenure:plans.tenure,
		// 					displayValue:plans.label
		// 				})

		// 			}
		// 			else{
		// 				tenureData.upfront.push({
		// 					tenure:plans.tenure,
		// 					displayValue:plans.label
		// 				})

		// 			}
		// 			tenureMap={...tenureMap,[plans?.tenure]:plans?.selectorLabel}
		// 		})
		// 	})
		// 	setTenureLabels(tenureMap)
		// 	// setTenures(tenureData)
		// }
	}, []);

	return (
		<>
			<Box
				mb={'24px'}
				sx={{
					borderRadius: '16px',
					border: '1px solid #E8E8E8',
					overflow: 'hidden',
				}}
			>
				<CartItemListHeader
					headerLabel={cartNameMap[props.cartType]}
					headerCount={props.cartItemsCount}
				/>
				<CartItemList
					screen={props?.screen}
					items={props.cartItems}
					vertical={props?.verticalCart}
					onDeleteClicked={deleteCartItemButtonClicked}
					viewSimilarClicked={props.openCartOosActionDrawer}
				/>
				<Divider />
				{props.verticalCart?.valueAddedServices &&
					props.verticalCart?.valueAddedServices?.length > 0 && (
						<>
							<Box p='0px 32px' mt='24px'>
								<Vas
									cartType={props.cartType}
									cartItems={props.cartItems}
									valueAddedServices={props.verticalCart?.valueAddedServices}
								/>
							</Box>
						</>
					)}
			</Box>
			<RemoveCartItemConfirmationDrawer
				open={!!selectedCartItemToDelete}
				onClose={closeDeleteCartItemConfirmation}
				itemToDelete={selectedCartItemToDelete}
				onConfirm={removeCartItem}
				onReject={wishlistItem}
				removingItem={removeCartItemViewState === ApiState.LOADING}
				movingItemToWishlist={moveToWishlistViewState === ApiState.LOADING}
			/>
		</>
	);
};

export default withFullScreenLoader(VerticalCart);
